/**
 * [G-A4] Глубокие ссылки — через QUERY, не через hash.
 *
 * Telegram дописывает `tgWebAppData=…&tgWebAppVersion=…` в тот же фрагмент URL.
 * Ссылка вида `…/#/sku/123` превращается в `#/sku/123&tgWebAppData=…`,
 * идентификатор разбирается в NaN, и пользователь видит 404.
 *
 * Правило: диплинк `…/?screen=ads&id=123`, приложение при старте переносит
 * параметры ЗАПРОСА в свой роутер.
 */

export interface Route {
  screen: string
  id: string | null
  params: Readonly<Record<string, string>>
}

export const DEFAULT_SCREEN = 'home'

/**
 * Разбирает маршрут из строки ЗАПРОСА (кнопка web_app бота: «…/?screen=r&id=…»). Фрагмент URL
 * сознательно игнорируется. Нет экрана в запросе — берётся startapp ссылки t.me: «r_<id>», «sl»
 * (Telegram допускает в нём только A–Z a–z 0–9 _ -, поэтому «код_параметр», [F-7.3.15]).
 */
export function parseRoute(url: string = window.location.href, start: string | null = null): Route {
  const u = new URL(url)
  const p = u.searchParams
  const params: Record<string, string> = {}
  for (const [k, v] of p) params[k] = v
  const q = p.get('screen')
  if (q === null && start !== null && /^[A-Za-z0-9_-]{1,64}$/.test(start)) {
    const i = start.indexOf('_')
    return { screen: i < 0 ? start : start.slice(0, i), id: i < 0 ? null : start.slice(i + 1), params }
  }
  return {
    screen: q ?? DEFAULT_SCREEN,
    id: p.get('id'),
    params,
  }
}

/**
 * Демонстрация дефекта: тот же маршрут, записанный во фрагмент.
 * Нужен для теста, доказывающего, ПОЧЕМУ hash использовать нельзя.
 */
export function parseRouteFromHashUnsafe(url: string): { screen: string; id: number } {
  // Так устроен типовой hash-роутер: режем путь по «/» и берём сегменты.
  // Именно здесь и ломается: Telegram дописывает свои параметры в тот же
  // фрагмент, последний сегмент становится «172845019&tgWebAppData=…»,
  // и Number() даёт NaN.
  const u = new URL(url)
  const seg = u.hash.replace(/^#\/?/, '').split('/')
  return { screen: seg[0] ?? DEFAULT_SCREEN, id: Number(seg[1]) }
}

export function buildDeepLink(bot: string, app: string, r: Partial<Route>): string {
  const p = new URLSearchParams()
  if (r.screen) p.set('screen', r.screen)
  if (r.id) p.set('id', r.id)
  // Канон 11: payload диплинка — не более 64 символов base64url,
  // поэтому внутрь кладётся короткий идентификатор состояния, а не JSON.
  const payload = p.toString()
  return `https://t.me/${bot}/${app}?startapp=${encodeURIComponent(payload)}`
}

/**
 * [G-A5] Глубина переходов. При входе по прямой ссылке history.back()
 * выбрасывает ИЗ приложения, поэтому на нуле ведём на главный экран.
 */
export class NavigationDepth {
  private depth = 0
  push(): void { this.depth++ }
  /** true — можно назад по истории; false — надо на главный экран. */
  canGoBack(): boolean { return this.depth > 0 }
  pop(): void { if (this.depth > 0) this.depth-- }
  reset(): void { this.depth = 0 }
  get value(): number { return this.depth }
}
